/* eslint-disable @next/next/no-img-element, @next/next/no-page-custom-font */
"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import Head from "next/head";
import Link from "next/link";

const BRIGHTCOVE_ACCOUNT = "5330942419001";

function formatTime(s: number) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return m + ":" + String(sec).padStart(2, "0");
}

let policyKeyCache: string | null = null;
const audioCache: Record<string, string> = {};

async function getPolicyKey() {
  if (policyKeyCache) return policyKeyCache;
  const res = await fetch(
    "https://players.brightcove.net/" +
      BRIGHTCOVE_ACCOUNT +
      "/default_default/index.min.js",
  );
  const text = await res.text();
  const match =
    text.match(/policyKey\s*:\s*["']([^"']+)["']/) ||
    text.match(/(BC[a-zA-Z0-9_-]{50,})/);
  if (match) policyKeyCache = match[1];
  return policyKeyCache;
}

async function loadAudioSrc(videoId: string) {
  if (audioCache[videoId]) return audioCache[videoId];
  const pk = await getPolicyKey();
  if (!pk) return null;
  const res = await fetch(
    "https://edge.api.brightcove.com/playback/v1/accounts/" +
      BRIGHTCOVE_ACCOUNT +
      "/videos/" +
      videoId,
    { headers: { Accept: "application/json;pk=" + pk } },
  );
  const data = await res.json();
  const mp4 = data.sources
    ?.filter(
      (s: { container?: string; type?: string }) =>
        s.container === "MP4" ||
        s.type === "video/mp4" ||
        s.type === "audio/mp4",
    )
    .sort(
      (a: { avg_bitrate?: number }, b: { avg_bitrate?: number }) =>
        (b.avg_bitrate || 0) - (a.avg_bitrate || 0),
    );
  const src = mp4?.[0]?.src || data.sources?.[0]?.src;
  if (src) audioCache[videoId] = src;
  return src || null;
}

function ArrowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 8l4 4-4 4" />
      <path d="M2 12h20" />
    </svg>
  );
}

function TestimonialCard({
  quote,
  name,
  role,
  variant,
  delay,
  className,
  style,
  quoteStyle,
}: {
  quote: string;
  name: string;
  role: string;
  variant: "dark" | "light";
  delay?: string;
  className?: string;
  style?: React.CSSProperties;
  quoteStyle?: React.CSSProperties;
}) {
  return (
    <div
      className={`testi-card ${variant} reveal${delay ? ` ${delay}` : ""}${className ? ` ${className}` : ""}`}
      style={style}
    >
      <p
        className={`testi-quote${variant === "light" ? " sm" : ""}`}
        style={quoteStyle}
      >
        {quote}
      </p>
      <div
        className="testi-attr"
        style={variant === "light" ? { marginTop: 24 } : undefined}
      >
        <p className={`testi-name${variant === "light" ? " dark-sm" : ""}`}>
          {name}
        </p>
        <p className="testi-role">{role}</p>
      </div>
    </div>
  );
}

type KnotData = {
  num: string;
  name: string;
  desc: string;
  delay: string;
  brightcoveId?: string;
};

const KNOTS_DATA: KnotData[] = [
  {
    num: "01",
    name: "Aversion",
    desc: "When something hurts, and the only move feels like pushing it away.",
    delay: "d1",
    brightcoveId: "6391100542112",
  },
  {
    num: "02",
    name: "Grasping",
    desc: "The thing you can\u2019t let go of. The worry that loops. The fear of losing what matters.",
    delay: "d2",
    brightcoveId: "6391100835112",
  },
  {
    num: "03",
    name: "Striving",
    desc: "One more thing to do. Rest feels risky. Stillness is hard to trust.",
    delay: "d3",
    brightcoveId: "6391099269112",
  },
  {
    num: "04",
    name: "Self-fixation",
    desc: "The inner commentary that never quiets. Am I enough? How did that land?",
    delay: "d4",
    brightcoveId: "6391100041112",
  },
  {
    num: "05",
    name: "Confusion",
    desc: "When it\u2019s all too much at once, and \u201CI don\u2019t know\u201D is the only honest answer.",
    delay: "d5",
    brightcoveId: "6391098786112",
  },
];

const BANNER_NAMES = [
  "Billboard",
  "The FADER",
  "Vice",
  "Nylon",
  "New York Magazine",
  "Afropunk",
  "Complex",
  "The Globe and Mail",
  "XXL",
  "Omega Institute",
  "Consciousness Explorers Club",
  "Happier Meditation",
  "Mind Bod Adventure Pod",
  "Broad City",
  "Animalogic",
  "Adidas",
  "SXSW",
];

export function TashaMarketingPage() {
  const meditationCardRef = useRef<HTMLDivElement>(null);
  const meditationAudioRef = useRef<HTMLAudioElement>(null);
  const progressFillRef = useRef<HTMLDivElement>(null);
  const progressThumbRef = useRef<HTMLDivElement>(null);
  const progressTrackRef = useRef<HTMLDivElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const timeCurrentRef = useRef<HTMLSpanElement>(null);
  const timeTotalRef = useRef<HTMLSpanElement>(null);
  const playIconRef = useRef<SVGSVGElement>(null);
  const pauseIconRef = useRef<SVGSVGElement>(null);
  const meditationLoadedRef = useRef(false);
  const isScrubbingRef = useRef(false);

  // Scroll reveal
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -32px 0px" },
    );
    document
      .querySelectorAll(
        ".tasha-marketing .reveal, .tasha-marketing .reveal-fade",
      )
      .forEach((el) => io.observe(el));

    // Trigger hero immediately
    setTimeout(() => {
      document
        .querySelectorAll(".tasha-marketing #hero .reveal")
        .forEach((el) => el.classList.add("visible"));
    }, 80);

    return () => io.disconnect();
  }, []);

  // Nav scroll effect
  useEffect(() => {
    const nav = document.querySelector(".tasha-marketing nav") as HTMLElement;
    if (!nav) return;
    const handler = () => {
      nav.style.background =
        window.scrollY > 40
          ? "rgba(245,240,235,0.97)"
          : "rgba(245,240,235,0.9)";
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Smooth scroll for anchor links
  useEffect(() => {
    const container = document.querySelector(".tasha-marketing");
    if (!container) return;
    const handler = (e: Event) => {
      const anchor = (e.target as HTMLElement).closest('a[href^="#"]');
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth" });
      }
    };
    container.addEventListener("click", handler);
    return () => container.removeEventListener("click", handler);
  }, []);

  // Mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handler = (e: MouseEvent) => {
      const menu = document.getElementById("mobileMenu");
      if (
        menu &&
        !menu.contains(e.target as Node) &&
        !(e.target as HTMLElement).closest(".nav-hamburger")
      ) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [mobileMenuOpen]);

  // Meditation player
  const updateProgress = useCallback(() => {
    const audio = meditationAudioRef.current;
    if (!audio || !audio.duration) return;
    const pct = (audio.currentTime / audio.duration) * 100;
    if (!isScrubbingRef.current) {
      if (progressFillRef.current)
        progressFillRef.current.style.width = pct + "%";
      if (progressThumbRef.current)
        progressThumbRef.current.style.left = pct + "%";
    }
    const bars = waveformRef.current?.querySelectorAll(".waveform-bar");
    if (bars) {
      const activeCount = Math.floor(
        (audio.currentTime / audio.duration) * bars.length,
      );
      bars.forEach((b, i) => b.classList.toggle("is-played", i < activeCount));
    }
    if (timeCurrentRef.current)
      timeCurrentRef.current.textContent = formatTime(audio.currentTime);
  }, []);

  const scrubTo = useCallback((clientX: number) => {
    const track = progressTrackRef.current;
    const audio = meditationAudioRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    if (progressFillRef.current)
      progressFillRef.current.style.width = pct * 100 + "%";
    if (progressThumbRef.current)
      progressThumbRef.current.style.left = pct * 100 + "%";
    if (audio && audio.duration) audio.currentTime = pct * audio.duration;
  }, []);

  const setMeditationPlaying = useCallback((playing: boolean) => {
    const card = meditationCardRef.current;
    if (!card) return;
    if (playing) {
      card.classList.add("is-playing");
      card.classList.remove("is-paused");
      if (playIconRef.current) playIconRef.current.style.display = "none";
      if (pauseIconRef.current) pauseIconRef.current.style.display = "block";
    } else {
      card.classList.add("is-paused");
      if (playIconRef.current) playIconRef.current.style.display = "block";
      if (pauseIconRef.current) pauseIconRef.current.style.display = "none";
    }
  }, []);

  const resetMeditationPlayer = useCallback(() => {
    const card = meditationCardRef.current;
    if (!card) return;
    card.classList.remove("is-playing", "is-paused");
    if (playIconRef.current) playIconRef.current.style.display = "block";
    if (pauseIconRef.current) pauseIconRef.current.style.display = "none";
    const bars = waveformRef.current?.querySelectorAll(".waveform-bar");
    bars?.forEach((b) => b.classList.remove("is-played"));
    if (progressFillRef.current) progressFillRef.current.style.width = "0%";
    if (progressThumbRef.current) progressThumbRef.current.style.left = "0%";
    if (timeCurrentRef.current) timeCurrentRef.current.textContent = "0:00";
  }, []);

  // Progress bar scrubbing
  useEffect(() => {
    const track = progressTrackRef.current;
    if (!track) return;

    const onMouseDown = (e: MouseEvent) => {
      isScrubbingRef.current = true;
      scrubTo(e.clientX);
      const onMove = (e: MouseEvent) => scrubTo(e.clientX);
      const onUp = () => {
        isScrubbingRef.current = false;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    };

    const onTouchStart = (e: TouchEvent) => {
      isScrubbingRef.current = true;
      scrubTo(e.touches[0].clientX);
      const onMove = (e: TouchEvent) => scrubTo(e.touches[0].clientX);
      const onEnd = () => {
        isScrubbingRef.current = false;
        document.removeEventListener("touchmove", onMove as EventListener);
        document.removeEventListener("touchend", onEnd);
      };
      document.addEventListener("touchmove", onMove as EventListener);
      document.addEventListener("touchend", onEnd);
    };

    track.addEventListener("mousedown", onMouseDown);
    track.addEventListener("touchstart", onTouchStart, { passive: true });
    return () => {
      track.removeEventListener("mousedown", onMouseDown);
      track.removeEventListener("touchstart", onTouchStart);
    };
  }, [scrubTo]);

  const handleMeditationClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = meditationAudioRef.current;
    if (!audio) return;

    if (!meditationLoadedRef.current) {
      const src = await loadAudioSrc("6391099655112");
      if (!src) return;
      audio.src = src;
      meditationLoadedRef.current = true;
    }
    if (audio.paused) {
      await audio.play();
      setMeditationPlaying(true);
    } else {
      audio.pause();
      setMeditationPlaying(false);
    }
  };

  return (
    <>
      <Head>
        <title>Weave × Tasha Schumann</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600;1,700&family=Space+Grotesk:wght@400;500;600&family=DM+Sans:opsz,wght@9,400;9,500&display=swap"
          rel="stylesheet"
        />
      </Head>
      <style>{`
/* ─── RESET ────────────────────────────────────────────── */
.tasha-marketing *,.tasha-marketing *::before,.tasha-marketing *::after{box-sizing:border-box;margin:0;padding:0}
.tasha-marketing{scroll-behavior:smooth}
.tasha-marketing img{display:block;max-width:100%}
.tasha-marketing button,.tasha-marketing input{font-family:inherit;border:none;outline:none;background:none;cursor:pointer}
.tasha-marketing a{text-decoration:none;color:inherit}
.tasha-marketing ul{list-style:none}

/* ─── TOKENS ───────────────────────────────────────────── */
.tasha-marketing{
  --bg:#f5f0eb;
  --bg-elevated:#ebe5df;
  --bg-dark:#0f0f0f;
  --bg-white:#ffffff;
  --text:#0f0f0f;
  --text-muted:#4f4f4f;
  --text-ghost:#878787;
  --text-on-dark:#f5f0eb;
  --text-on-dark-dim:rgba(250,246,240,0.7);
  --red:#ba0110;
  --red-light:#e74451;
  --border:#e0d6cf;
  --border-muted:#ccc4bf;
  --border-dark:rgba(224,214,207,0.16);
  --shadow-light:0 4px 24px rgba(0,0,0,0.05);
  --shadow-heavy:0 4px 42px rgba(0,0,0,0.15);
  --px:80px;
  --nav-h:79px;
}

/* ─── TYPE SYSTEM ──────────────────────────────────────── */
.tasha-marketing .cg{font-family:'Cormorant Garamond',Georgia,serif}
.tasha-marketing .sg{font-family:'Space Grotesk',sans-serif}
.tasha-marketing .hv{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif}
.tasha-marketing .dm{font-family:'DM Sans',sans-serif}

/* Labels */
.tasha-marketing .label-md{font-family:'Space Grotesk',sans-serif;font-size:12px;font-weight:500;line-height:16px;letter-spacing:3px;text-transform:uppercase}
.tasha-marketing .label-sm{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;line-height:14px;letter-spacing:1.5px;text-transform:uppercase}
.tasha-marketing .label-xs{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;line-height:14px;letter-spacing:0.5px}

/* Titles */
.tasha-marketing .title-xl{font-family:'Cormorant Garamond',serif;font-size:72px;font-weight:700;line-height:80px;letter-spacing:-2px}
.tasha-marketing .title-lg{font-family:'Cormorant Garamond',serif;font-size:64px;font-weight:600;line-height:68px;letter-spacing:-1px}
.tasha-marketing .title-md{font-family:'Cormorant Garamond',serif;font-size:50px;font-weight:600;line-height:54px;letter-spacing:-1px}
.tasha-marketing .title-xs{font-family:'Cormorant Garamond',serif;font-size:36px;font-weight:600;line-height:42px;letter-spacing:-0.36px}

/* Headers */
.tasha-marketing .h1{font-family:'Cormorant Garamond',serif;font-size:28px;font-weight:700;line-height:32px;letter-spacing:-0.56px}
.tasha-marketing .h2{font-family:'Cormorant Garamond',serif;font-size:24px;font-weight:700;line-height:28px;letter-spacing:-0.48px}
.tasha-marketing .h3{font-family:'Cormorant Garamond',serif;font-size:20px;font-weight:700;line-height:24px;letter-spacing:-0.5px}
.tasha-marketing .h4{font-family:'Cormorant Garamond',serif;font-size:16px;font-weight:700;line-height:24px}

/* Body */
.tasha-marketing .body-xl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:20px;font-weight:400;line-height:32px}
.tasha-marketing .body-lg{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:18px;font-weight:400;line-height:28px}
.tasha-marketing .body-md{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:16px;font-weight:400;line-height:26px}
.tasha-marketing .body-sm{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;font-weight:400;line-height:22px}

/* ─── ANIMATIONS ───────────────────────────────────────── */
@keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
@keyframes shimmer{0%{opacity:0.6}50%{opacity:1}100%{opacity:0.6}}
@keyframes driftNear{0%,100%{transform:translate(0,0) scale(1)}30%{transform:translate(5px,-3px) scale(1.02)}70%{transform:translate(-3px,5px) scale(0.98)}}
@keyframes driftFar{0%,100%{transform:translate(0,0) scale(1)}40%{transform:translate(-4px,6px) scale(0.97)}80%{transform:translate(3px,-3px) scale(1.02)}}
@keyframes driftMid{0%,100%{transform:translate(0,0) scale(1)}25%{transform:translate(3px,5px) scale(1.02)}60%{transform:translate(-5px,-3px) scale(0.98)}}

.tasha-marketing .reveal{opacity:0;transform:translateY(20px);transition:opacity 0.75s cubic-bezier(0.16,1,0.3,1),transform 0.75s cubic-bezier(0.16,1,0.3,1)}
.tasha-marketing .reveal.visible{opacity:1;transform:none}
.tasha-marketing .reveal-fade{opacity:0;transition:opacity 0.9s ease}
.tasha-marketing .reveal-fade.visible{opacity:1}
.tasha-marketing .d1{transition-delay:0.08s}
.tasha-marketing .d2{transition-delay:0.16s}
.tasha-marketing .d3{transition-delay:0.24s}
.tasha-marketing .d4{transition-delay:0.34s}
.tasha-marketing .d5{transition-delay:0.44s}

/* ─── LAYOUT ───────────────────────────────────────────── */
.tasha-marketing{overflow-x:hidden;background:var(--bg);color:var(--text);-webkit-font-smoothing:antialiased}
.tasha-marketing .container{max-width:1440px;margin:0 auto;padding:0 var(--px)}

/* ─── NAV ──────────────────────────────────────────────── */
.tasha-marketing nav{
  position:fixed;top:0;left:0;right:0;z-index:100;
  height:var(--nav-h);
  display:flex;align-items:center;justify-content:space-between;
  padding:0 48px;
  background:rgba(245,240,235,0.9);
  backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);
  border-bottom:1px solid var(--border);
  transition:background 0.3s ease;
}
.tasha-marketing .nav-logo{display:flex;align-items:center;gap:16px}
.tasha-marketing .nav-logo img{height:27px;width:90px;object-fit:contain}
.tasha-marketing .nav-logo-sep{display:flex;align-items:center;gap:16px;padding:12px 0 4px}
.tasha-marketing .nav-logo-x{width:7px;height:7px}
.tasha-marketing .nav-tasha{font-family:'Space Grotesk',sans-serif;font-size:12px;font-weight:500;letter-spacing:3px;text-transform:uppercase;color:var(--red)}
.tasha-marketing .nav-links{display:flex;gap:48px}
.tasha-marketing .nav-links a{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;color:var(--text);transition:opacity 0.2s}
.tasha-marketing .nav-links a:hover{opacity:0.6}
.tasha-marketing .btn-primary{
  display:inline-flex;align-items:center;gap:12px;
  height:46px;padding:12px 24px 12px 36px;
  background:var(--bg-dark);border-radius:9999px;
  font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;
  color:var(--text-on-dark);
  transition:opacity 0.2s,transform 0.2s;
  white-space:nowrap;
}
.tasha-marketing .btn-primary:hover{opacity:0.85;transform:translateY(-1px)}
.tasha-marketing .btn-primary img,.tasha-marketing .btn-primary svg{width:16px;height:16px}
.tasha-marketing .btn-secondary{
  display:inline-flex;align-items:center;gap:12px;
  height:48px;padding:12px 36px;
  background:var(--bg);border:1px solid var(--text);border-radius:9999px;
  font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;
  color:var(--text);
  transition:background 0.2s,transform 0.2s;
  white-space:nowrap;
}
.tasha-marketing .btn-secondary:hover{background:#ebe5df;transform:translateY(-1px)}
.tasha-marketing .btn-secondary img,.tasha-marketing .btn-secondary svg{width:16px;height:16px}
.tasha-marketing .btn-outline-dark{
  display:inline-flex;align-items:center;gap:12px;
  height:48px;padding:12px 36px;
  background:var(--bg);border:1px solid var(--text);border-radius:9999px;
  font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;
  color:var(--text);
  transition:background 0.2s;
}
.tasha-marketing .btn-outline-dark:hover{background:#ebe5df}
.tasha-marketing .btn-outline-dark img,.tasha-marketing .btn-outline-dark svg{width:16px;height:16px}
.tasha-marketing .nav-hamburger{display:none;flex-direction:column;gap:5px;padding:8px;cursor:pointer}
.tasha-marketing .nav-hamburger span{display:block;width:22px;height:1.5px;background:var(--text);border-radius:2px;transition:all 0.3s}
.tasha-marketing .mobile-menu{display:none;position:fixed;top:var(--nav-h);left:0;right:0;background:var(--bg);border-bottom:1px solid var(--border);padding:24px 48px;z-index:99;flex-direction:column;gap:24px;animation:fadeIn 0.2s ease}
.tasha-marketing .mobile-menu.open{display:flex}
.tasha-marketing .mobile-menu a{font-family:'Space Grotesk',sans-serif;font-size:14px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;color:var(--text)}

/* ─── EYEBROW ──────────────────────────────────────────── */
.tasha-marketing .eyebrow{display:flex;align-items:center;gap:10px;padding:8px 0}
.tasha-marketing .eyebrow-line{width:36px;height:1px;background:currentColor;flex-shrink:0}
.tasha-marketing .eyebrow-text{font-family:'Space Grotesk',sans-serif;font-size:12px;font-weight:500;letter-spacing:3px;text-transform:uppercase;white-space:nowrap}

/* ─── HERO ─────────────────────────────────────────────── */
.tasha-marketing #hero{
  padding-top:var(--nav-h);
  min-height:100vh;
  position:relative;
  overflow:hidden;
  display:flex;align-items:center;
}
.tasha-marketing .hero-inner{
  width:100%;max-width:1440px;margin:0 auto;
  padding:0 var(--px);
  display:flex;
  gap:40px;
  align-items:center;
  min-height:calc(100vh - var(--nav-h));
}
.tasha-marketing .hero-left{display:flex;flex-direction:column;gap:36px;padding:60px 0;flex:1 1 420px;min-width:0}
.tasha-marketing .hero-right{flex:1 1 300px;min-width:0}
.tasha-marketing .hero-copy{display:flex;flex-direction:column;gap:24px}
.tasha-marketing .hero-h1{font-family:'Cormorant Garamond',serif;font-size:72px;font-weight:700;line-height:80px;letter-spacing:-2px;color:var(--text)}
.tasha-marketing .hero-h1 em{font-style:italic;color:var(--red)}
.tasha-marketing .hero-sub{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:20px;font-weight:400;line-height:32px;color:var(--text-muted)}
.tasha-marketing .hero-sub strong{font-weight:700;color:var(--text-muted)}

/* Meditation card */
.tasha-marketing .meditation-card{
  width:100%;max-width:462px;
  background:white;border:1px solid var(--border-muted);border-radius:24px;
  padding:17px;display:flex;gap:12px;align-items:center;
  box-shadow:var(--shadow-light);
  transition:box-shadow 0.3s,transform 0.3s;
}
.tasha-marketing .meditation-card:hover{box-shadow:0 8px 32px rgba(0,0,0,0.1);transform:translateY(-2px)}
.tasha-marketing .meditation-card-blob{width:70px;height:70px;flex-shrink:0;border-radius:12px;overflow:hidden}
.tasha-marketing .meditation-card-blob img{width:100%;height:100%;object-fit:cover}
.tasha-marketing .meditation-card-info{flex:1;display:flex;flex-direction:column;gap:8px}
.tasha-marketing .meditation-card-title{font-family:'Cormorant Garamond',serif;font-size:28px;font-weight:700;line-height:32px;letter-spacing:-0.56px}
.tasha-marketing .meditation-card-artist{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase}
.tasha-marketing .meditation-card-meta{display:flex;gap:9px;align-items:center;font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-ghost)}
.tasha-marketing .meditation-card-btn{
  width:36px;height:36px;flex-shrink:0;
  border:1px solid var(--text);border-radius:9999px;
  display:flex;align-items:center;justify-content:center;
  transition:background 0.2s;
}
.tasha-marketing .meditation-card-btn:hover{background:var(--text)}
.tasha-marketing .meditation-card-btn:hover img,.tasha-marketing .meditation-card-btn:hover svg{filter:invert(1)}
.tasha-marketing .meditation-card-btn img,.tasha-marketing .meditation-card-btn svg{width:14px;height:14px}

/* Playing state */
.tasha-marketing .meditation-card.is-playing .meditation-card-meta-default{display:none}
.tasha-marketing .meditation-card-waveform{display:none;align-items:flex-end;gap:2px;height:24px;flex:1}
.tasha-marketing .meditation-card.is-playing .meditation-card-waveform{display:flex}
.tasha-marketing .waveform-bar{width:3px;background:rgba(0,0,0,0.15);border-radius:2px;animation:waveform 1.2s ease-in-out infinite alternate}
.tasha-marketing .waveform-bar:nth-child(1){height:40%;animation-delay:0s}
.tasha-marketing .waveform-bar:nth-child(2){height:70%;animation-delay:0.1s}
.tasha-marketing .waveform-bar:nth-child(3){height:50%;animation-delay:0.2s}
.tasha-marketing .waveform-bar:nth-child(4){height:90%;animation-delay:0.05s}
.tasha-marketing .waveform-bar:nth-child(5){height:35%;animation-delay:0.25s}
.tasha-marketing .waveform-bar:nth-child(6){height:65%;animation-delay:0.15s}
.tasha-marketing .waveform-bar:nth-child(7){height:45%;animation-delay:0.3s}
.tasha-marketing .waveform-bar:nth-child(8){height:80%;animation-delay:0.08s}
.tasha-marketing .waveform-bar:nth-child(9){height:55%;animation-delay:0.18s}
.tasha-marketing .waveform-bar:nth-child(10){height:40%;animation-delay:0.22s}
.tasha-marketing .waveform-bar:nth-child(11){height:75%;animation-delay:0.12s}
.tasha-marketing .waveform-bar:nth-child(12){height:50%;animation-delay:0.28s}
.tasha-marketing .waveform-bar:nth-child(13){height:60%;animation-delay:0.06s}
.tasha-marketing .waveform-bar:nth-child(14){height:85%;animation-delay:0.16s}
.tasha-marketing .waveform-bar:nth-child(15){height:45%;animation-delay:0.24s}
.tasha-marketing .waveform-bar:nth-child(16){height:70%;animation-delay:0.1s}
.tasha-marketing .waveform-bar.is-played{background:rgba(0,0,0,0.6)}
@keyframes waveform{0%{transform:scaleY(0.4)}100%{transform:scaleY(1)}}
.tasha-marketing .meditation-card.is-paused .waveform-bar{animation-play-state:paused}
.tasha-marketing .meditation-card-progress{display:none;width:100%;margin-top:4px}
.tasha-marketing .meditation-card.is-playing .meditation-card-progress{display:block}
.tasha-marketing .meditation-card-progress-track{position:relative;width:100%;height:4px;background:rgba(0,0,0,0.08);border-radius:2px;cursor:pointer}
.tasha-marketing .meditation-card-progress-fill{height:100%;background:var(--text);border-radius:2px;width:0%;transition:width 0.25s linear}
.tasha-marketing .meditation-card-progress-thumb{position:absolute;top:50%;left:0%;width:10px;height:10px;background:var(--text);border-radius:50%;transform:translate(-50%,-50%);opacity:0;transition:opacity 0.2s}
.tasha-marketing .meditation-card-progress-track:hover .meditation-card-progress-thumb{opacity:1}
.tasha-marketing .meditation-card-progress-time{display:flex;justify-content:space-between;margin-top:4px;font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1px;color:var(--text-ghost)}

/* Mobile-only hero photo (hidden on desktop/tablet) */
.tasha-marketing .hero-mobile-photo{display:none;position:relative;width:130px;height:130px}
.tasha-marketing .hero-mobile-photo .hero-mobile-shadow{position:absolute;inset:0;width:100%;height:100%}
.tasha-marketing .hero-mobile-photo img{position:absolute;inset:4%;width:92%;height:92%;object-fit:contain}

.tasha-marketing .hero-ctas{display:flex;gap:12px;flex-wrap:wrap}
.tasha-marketing .hero-stats{display:flex;gap:24px;border-top:1px solid var(--border);padding-top:25px}
.tasha-marketing .stat-num{font-family:'Cormorant Garamond',serif;font-size:24px;font-weight:700;line-height:28px;letter-spacing:-0.48px;color:#1a0f06;margin-bottom:6px}
.tasha-marketing .stat-label{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-ghost)}

/* Hero right */
.tasha-marketing .hero-right{position:relative;aspect-ratio:1/1.05;max-height:712px}
.tasha-marketing .hero-blob-shadow{position:absolute;width:100%;aspect-ratio:1;left:0;top:0;z-index:0}
.tasha-marketing .hero-blob-shadow svg{width:100%;height:100%}
.tasha-marketing .hero-blob-bg{position:absolute;width:100%;aspect-ratio:603/563;left:0;top:0;z-index:1}
.tasha-marketing .hero-blob-bg img{width:100%;height:100%;object-fit:contain}
.tasha-marketing .hero-flowers{position:absolute;left:0;bottom:0;width:177px;height:371px;mix-blend-mode:darken;opacity:0.6;z-index:2;pointer-events:none}
.tasha-marketing .hero-flowers img{width:100%;height:100%;object-fit:cover}
.tasha-marketing .hero-img-decor{position:absolute;top:-10px;right:-48px;width:371px;height:371px;mix-blend-mode:darken;opacity:0.4;z-index:0;pointer-events:none}
.tasha-marketing .hero-img-decor img{width:100%;height:100%;object-fit:cover}

/* Observation card */
.tasha-marketing .obs-card{
  position:absolute;
  bottom:10px;left:10px;
  width:293px;
  background:white;border:1px solid var(--border-muted);border-radius:24px;
  padding:24px;
  box-shadow:var(--shadow-light);
  z-index:3;
  display:flex;flex-direction:column;gap:12px;
  animation:float 6s ease-in-out infinite;
  transition:box-shadow 0.3s,transform 0.3s;
}
.tasha-marketing .obs-card:hover{box-shadow:0 12px 40px rgba(0,0,0,0.12)}
.tasha-marketing .obs-eyebrow{display:flex;align-items:center;gap:12px}
.tasha-marketing .obs-eyebrow-text{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.4px;text-transform:uppercase;color:var(--text-muted);white-space:nowrap}
.tasha-marketing .obs-eyebrow-line{width:32px;flex:none;height:1px;background:var(--text-muted)}
.tasha-marketing .obs-dot{position:absolute;top:29px;right:25px;width:6px;height:6px;border-radius:50%;background:var(--red)}
.tasha-marketing .obs-quote{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:16px;line-height:26px;color:var(--text-muted)}
.tasha-marketing .obs-sig{font-family:'Cormorant Garamond',serif;font-size:20px;font-style:italic;line-height:24px;letter-spacing:-0.5px;color:var(--text)}

/* ─── BANNER ────────────────────────────────────────────── */
.tasha-marketing #banner{
  background:var(--bg-dark);
  padding:48px 0;
  overflow:hidden;
  position:relative;
  z-index:3;
}
.tasha-marketing .banner-label{font-family:'Space Grotesk',sans-serif;font-size:12px;font-weight:500;letter-spacing:3px;text-transform:uppercase;color:rgba(250,246,240,0.6);text-align:center;margin-bottom:24px}
.tasha-marketing .banner-track-wrap{overflow:hidden}
.tasha-marketing .banner-track{display:flex;gap:36px;align-items:center;animation:marquee 60s linear infinite;width:max-content}
.tasha-marketing .banner-pill{
  display:flex;align-items:center;
  height:38px;padding:9px 25px;
  border:1px solid rgba(250,246,240,0.3);border-radius:9999px;
  font-family:'DM Sans',sans-serif;font-size:14px;color:#faf6f0;
  white-space:nowrap;
  transition:border-color 0.3s,background 0.3s;
  flex-shrink:0;
}
.tasha-marketing .banner-pill:hover{border-color:rgba(250,246,240,0.7);background:rgba(250,246,240,0.08)}

/* ─── APP FEATURES ──────────────────────────────────────── */
.tasha-marketing #app{
  position:relative;padding:104px var(--px) 104px;
  overflow:hidden;
}
.tasha-marketing .app-bg{position:absolute;inset:0;z-index:0;pointer-events:none}
.tasha-marketing .app-bg::before{content:'';position:absolute;inset:0;background:var(--bg)}
.tasha-marketing .app-bg-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0.09}
.tasha-marketing .app-bg-grad{position:absolute;inset:0;background:linear-gradient(to right,rgba(245,240,235,0.8) 7%,rgba(245,240,235,0.16) 93%)}
.tasha-marketing .app-inner{position:relative;z-index:1;display:flex;gap:80px;align-items:flex-start;justify-content:space-between;max-width:1440px;margin:0 auto}
.tasha-marketing .app-copy{display:flex;flex-direction:column;gap:24px;max-width:640px}
.tasha-marketing .app-h{font-family:'Cormorant Garamond',serif;font-size:64px;font-weight:600;line-height:68px;letter-spacing:-1px;color:var(--text)}
.tasha-marketing .app-h em{font-style:italic;color:var(--red)}
.tasha-marketing .app-p{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:18px;line-height:28px;color:var(--text-muted)}
.tasha-marketing .highlights{display:flex;flex-direction:column;gap:24px}
.tasha-marketing .highlight{
  border-left:4px solid var(--text);
  padding-left:24px;
  display:flex;flex-direction:column;gap:4px;
  transition:border-color 0.3s;
}
.tasha-marketing .highlight:hover{border-color:var(--red)}
.tasha-marketing .highlight-title{font-family:'Cormorant Garamond',serif;font-size:24px;font-weight:700;line-height:28px;letter-spacing:-0.48px;white-space:nowrap}
.tasha-marketing .highlight-desc{font-family:'DM Sans',sans-serif;font-size:16px;line-height:24px;color:var(--text-ghost)}
.tasha-marketing .quote-card{
  background:white;padding:24px 28px;
  display:flex;flex-direction:column;gap:12px;
  transition:box-shadow 0.3s;
}
.tasha-marketing .quote-card:hover{box-shadow:var(--shadow-light)}
.tasha-marketing .quote-card-text{font-family:'Cormorant Garamond',serif;font-size:20px;font-style:italic;line-height:24px;letter-spacing:-0.5px;color:var(--text)}
.tasha-marketing .quote-card-attr{font-family:'Space Grotesk',sans-serif;font-size:12px;font-weight:500;letter-spacing:3px;text-transform:uppercase;color:var(--red)}

/* App images */
.tasha-marketing .app-images-left,.tasha-marketing .app-images-right{display:contents}
.tasha-marketing .app-images{position:relative;width:648px;height:776px;flex-shrink:0}
.tasha-marketing .app-img-77{position:absolute;top:40px;left:22px;width:311px;height:255px;overflow:hidden;animation:driftNear 14s ease-in-out infinite;z-index:2}
.tasha-marketing .app-img-77 img{width:100%;height:100%;object-fit:cover}
.tasha-marketing .app-img-74{position:absolute;top:51px;left:341px;width:307px;height:661px;border:1px solid var(--border-muted);border-radius:24px;overflow:hidden;box-shadow:var(--shadow-heavy)}
.tasha-marketing .app-img-74 img{width:100%;height:100%;object-fit:cover;border-radius:24px}
.tasha-marketing .app-img-76{position:absolute;top:475px;left:42px;width:280px;height:203px;overflow:hidden;box-shadow:var(--shadow-light);animation:driftFar 18s ease-in-out infinite;z-index:2}
.tasha-marketing .app-img-76 img{width:100%;height:100%;object-fit:cover}
.tasha-marketing .app-img-75{position:absolute;top:313px;left:121px;width:266px;height:125px;overflow:hidden;box-shadow:var(--shadow-light);animation:driftMid 16s ease-in-out infinite;z-index:3}
.tasha-marketing .app-img-75 img{width:100%;height:100%;object-fit:cover}

/* ─── TASHA SECTION ─────────────────────────────────────── */
.tasha-marketing #tasha-section{position:relative;height:934px;overflow:visible}
.tasha-marketing .tasha-bg{position:absolute;left:-158px;top:0;width:calc(100% + 316px);height:100%}
.tasha-marketing .tasha-bg img{width:100%;height:100%;object-fit:cover}
.tasha-marketing .tasha-card{
  position:absolute;top:10%;right:0;
  width:646px;
  background:rgba(0,0,0,0.6);
  backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);
  border-radius:24px;padding:36px;
  display:flex;flex-direction:column;gap:24px;
  margin-right:var(--px);
}
.tasha-marketing .tasha-h{font-family:'Cormorant Garamond',serif;font-size:64px;font-weight:600;line-height:68px;letter-spacing:-1px;color:var(--text-on-dark)}
.tasha-marketing .tasha-h em{font-style:italic}
.tasha-marketing .tasha-body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:18px;line-height:28px;color:var(--text-on-dark);white-space:pre-wrap}
.tasha-marketing .tasha-body strong{font-weight:700}
.tasha-marketing .tasha-body em{font-style:italic}
.tasha-marketing .tasha-pills-wrap{display:flex;flex-direction:column;gap:12px}
.tasha-marketing .tasha-pills-label{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:rgba(245,240,235,0.7)}
.tasha-marketing .tasha-pills{display:flex;flex-wrap:wrap;gap:12px}
.tasha-marketing .tasha-pill{
  background:rgba(245,240,235,0.12);
  border-radius:36px;padding:8px 24px;
  font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;
  color:var(--text-on-dark);
  transition:background 0.2s;
}
.tasha-marketing .tasha-pill:hover{background:rgba(245,240,235,0.2)}

/* Lama card */
.tasha-marketing .lama-card{
  position:absolute;bottom:-60px;left:var(--px);
  width:254px;
  background:white;border:1px solid var(--border-muted);border-radius:24px;padding:24px;
  box-shadow:var(--shadow-light);
  display:flex;flex-direction:column;gap:12px;
  z-index:4;
  animation:float 6s ease-in-out infinite;
}
.tasha-marketing .lama-card-label{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.4px;text-transform:uppercase;color:var(--text-muted)}
.tasha-marketing .lama-card ul{padding-left:20px;list-style:disc;display:flex;flex-direction:column;gap:0}
.tasha-marketing .lama-card ul li{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:16px;line-height:26px;color:var(--text-muted)}

/* ─── KNOTS ─────────────────────────────────────────────── */
.tasha-marketing #knots{background:var(--bg-dark);padding:128px var(--px);padding-top:160px;display:flex;flex-direction:column;gap:56px;position:relative;z-index:1}
.tasha-marketing .knots-copy{display:flex;flex-direction:column;gap:16px;align-items:center;text-align:center;padding:0 19px}
.tasha-marketing .knots-eyebrow{color:var(--red-light)}
.tasha-marketing .knots-h{font-family:'Cormorant Garamond',serif;font-size:50px;font-weight:600;line-height:54px;letter-spacing:-1px;color:#faf6f0;white-space:nowrap}
.tasha-marketing .knots-h em{font-style:italic}
.tasha-marketing .knots-p{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:18px;line-height:28px;color:rgba(250,246,240,0.7);max-width:731px;text-align:center}
.tasha-marketing .knots-grid{display:flex;gap:36px}
.tasha-marketing .knot-item{flex:1;display:flex;flex-direction:column;gap:16px;border-top:1px solid var(--border-dark);padding-top:24px}
.tasha-marketing .knot-num{font-family:'Cormorant Garamond',serif;font-size:36px;font-weight:600;font-style:italic;line-height:42px;letter-spacing:-0.36px;color:var(--red)}
.tasha-marketing .knot-name{font-family:'Cormorant Garamond',serif;font-size:36px;font-weight:600;line-height:42px;letter-spacing:-0.36px;color:#f5f0eb;white-space:nowrap}
.tasha-marketing .knot-desc{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:16px;line-height:26px;color:#f5f0eb;opacity:0.8}
.tasha-marketing .knot-play{display:flex;align-items:center;gap:12px;margin-top:4px}
.tasha-marketing .knot-play-btn{
  width:36px;height:36px;
  border:1px solid rgba(245,240,235,0.3);border-radius:9999px;
  display:flex;align-items:center;justify-content:center;
  flex-shrink:0;
  color:#f5f0eb;
  transition:border-color 0.2s,background 0.2s;
}
.tasha-marketing .knot-play-btn:hover{border-color:rgba(224,214,207,0.5);background:rgba(255,255,255,0.08)}
.tasha-marketing .knot-play-btn img,.tasha-marketing .knot-play-btn svg{width:14px;height:14px}
.tasha-marketing .knot-play-label{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;color:#f5f0eb;opacity:0.5;font-variant-numeric:tabular-nums}
.tasha-marketing .knot-play.is-playing .knot-icon-play{display:none}
.tasha-marketing .knot-play.is-playing .knot-icon-pause{display:block!important}
.tasha-marketing .knot-play.is-playing .knot-play-btn{border-color:rgba(245,240,235,0.6);background:rgba(255,255,255,0.08)}
.tasha-marketing .knots-cta{display:flex;flex-direction:column;gap:16px;align-items:center}
.tasha-marketing .knots-cta-note{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:rgba(250,246,240,0.6);text-align:center}

/* ─── WHAT YOU GET ──────────────────────────────────────── */
.tasha-marketing #how{background:var(--bg);padding:128px var(--px) 64px}
.tasha-marketing .how-header{display:flex;flex-direction:column;gap:16px;align-items:center;text-align:center;margin-bottom:64px}
.tasha-marketing .how-h{font-family:'Cormorant Garamond',serif;font-size:50px;font-weight:600;line-height:54px;letter-spacing:-1px;color:#0a0a0a;white-space:nowrap}
.tasha-marketing .how-h em{font-style:italic}
.tasha-marketing .how-p{font-family:'DM Sans',sans-serif;font-size:18px;line-height:28px;color:rgba(26,15,6,0.8);max-width:768px;text-align:center}
.tasha-marketing .steps{position:relative;display:flex;gap:32px;align-items:flex-start}
.tasha-marketing .step{flex:1;display:flex;flex-direction:column;gap:8px;align-items:center;z-index:1}
.tasha-marketing .step-image{position:relative;width:100%;max-width:clamp(253px,18vw,320px);aspect-ratio:253/334;border-radius:24px;overflow:hidden;transition:transform 0.6s ease;transform-origin:center center}
.tasha-marketing .step-image:hover{transform:scale(1.05)}
.tasha-marketing .step-image img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block}
.tasha-marketing .step-image-scrim{position:absolute;bottom:0;left:0;right:0;height:90px;background:linear-gradient(to top,#f5f0eb 25.84%,rgba(245,240,235,0));pointer-events:none;z-index:1}
.tasha-marketing .step-copy{display:flex;flex-direction:column;gap:12px;align-items:center;width:100%}
.tasha-marketing .step-title{font-family:'Cormorant Garamond',serif;font-size:24px;font-weight:700;line-height:28px;letter-spacing:-0.48px;text-align:center}
.tasha-marketing .step-desc{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:16px;line-height:26px;color:var(--text-muted);text-align:center}
.tasha-marketing .step-tag{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;color:var(--text);text-align:center}
.tasha-marketing .steps-connector{position:absolute;top:calc(clamp(253px,18vw,320px) * 0.66 - 80px);left:calc(25% + 16px);right:calc(25% + 16px);height:1px;background:var(--border);pointer-events:none;z-index:0}
.tasha-marketing .steps-connector::before,.tasha-marketing .steps-connector::after{content:'';position:absolute;top:50%;transform:translateY(-50%);width:6px;height:6px;border-radius:50%;background:var(--border)}
.tasha-marketing .steps-connector::before{left:-3px}
.tasha-marketing .steps-connector::after{right:-3px}

/* ─── TESTIMONIALS ──────────────────────────────────────── */
.tasha-marketing #testimonials{background:var(--bg-elevated);padding:128px var(--px)}
.tasha-marketing .testi-header{display:flex;flex-direction:column;gap:16px;align-items:center;text-align:center;margin-bottom:64px}
.tasha-marketing .testi-h{font-family:'Cormorant Garamond',serif;font-size:50px;font-weight:600;line-height:54px;letter-spacing:-1px;color:#0a0a0a;white-space:nowrap}
.tasha-marketing .testi-grid{position:relative;display:grid;gap:24px}
.tasha-marketing .testi-row1{display:grid;grid-template-columns:2fr 1fr;grid-template-rows:1fr 1fr;gap:24px}
.tasha-marketing .testi-big{grid-row:1/-1}
.tasha-marketing .testi-row2{display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px}
.tasha-marketing .testi-card{
  border-radius:16px;padding:32px;
  display:flex;flex-direction:column;justify-content:space-between;
  transition:transform 0.3s cubic-bezier(0.16,1,0.3,1),box-shadow 0.3s;
}
.tasha-marketing .testi-card:hover{transform:translateY(-4px);box-shadow:0 12px 40px rgba(0,0,0,0.08)}
.tasha-marketing .testi-card.dark{background:var(--bg-dark)}
.tasha-marketing .testi-card.light{background:white}
.tasha-marketing .testi-quote{font-family:'Cormorant Garamond',serif;font-style:italic;line-height:46px;letter-spacing:-0.36px;color:var(--text-on-dark)}
.tasha-marketing .testi-quote.sm{font-size:20px;line-height:28px;letter-spacing:-0.5px;color:#0a0a0a}
.tasha-marketing .testi-attr{display:flex;flex-direction:column;gap:0}
.tasha-marketing .testi-name{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-on-dark);margin-top:16px}
.tasha-marketing .testi-name.dark-sm{color:var(--text-muted)}
.tasha-marketing .testi-role{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-ghost)}

/* ─── WAITLIST ──────────────────────────────────────────── */
.tasha-marketing #waitlist{
  background:var(--bg-dark);
  position:relative;overflow:hidden;
  padding:128px 48px;
  display:flex;flex-direction:column;gap:10px;
  align-items:center;
}
.tasha-marketing .waitlist-glow{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:890px;height:554px;background:radial-gradient(ellipse at 50% 50%,rgba(255,255,255,0.08) 0%,rgba(255,255,255,0.02) 40%,transparent 70%);pointer-events:none}
.tasha-marketing .waitlist-inner{position:relative;z-index:1;max-width:896px;width:100%;display:flex;flex-direction:column;gap:24px;align-items:center}
.tasha-marketing .waitlist-h{font-family:'Cormorant Garamond',serif;font-size:50px;font-weight:600;line-height:54px;letter-spacing:-1px;color:var(--text-on-dark);text-align:center}
.tasha-marketing .waitlist-p{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:18px;line-height:28px;color:var(--text-on-dark);text-align:center;max-width:869px}
.tasha-marketing .waitlist-form{display:flex;gap:16px;height:48px;width:100%;max-width:448px;margin-bottom:0}
.tasha-marketing .waitlist-input{
  flex:1;
  background:rgba(255,255,255,0.1);border:1px solid rgba(224,214,207,0.16);border-radius:9999px;
  padding:16px 24px;
  font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:16px;line-height:26px;
  color:var(--text-ghost);
  transition:border-color 0.2s,background 0.2s;
}
.tasha-marketing .waitlist-input::placeholder{color:var(--text-ghost)}
.tasha-marketing .waitlist-input:focus{border-color:rgba(224,214,207,0.4);background:rgba(255,255,255,0.14);color:#f5f0eb;outline:none}
.tasha-marketing .waitlist-btn{
  display:inline-flex;align-items:center;justify-content:center;gap:12px;
  height:48px;padding:12px 36px;
  background:var(--bg);border:1px solid var(--text);border-radius:9999px;
  font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;
  color:var(--text);white-space:nowrap;flex-shrink:0;
  transition:background 0.2s,transform 0.2s;
}
.tasha-marketing .waitlist-btn:hover{background:#ebe5df;transform:translateY(-1px)}
.tasha-marketing .waitlist-btn img,.tasha-marketing .waitlist-btn svg{width:16px;height:16px}
.tasha-marketing .waitlist-fine{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:rgba(245,240,235,0.6);text-align:center;margin-top:14px}

/* ─── FOOTER ────────────────────────────────────────────── */
.tasha-marketing footer{
  background:var(--bg);
  border-top:1px solid var(--border);
  height:128px;
  display:flex;align-items:center;justify-content:space-between;
  padding:0 var(--px);
}
.tasha-marketing .footer-logo{display:flex;align-items:center;gap:16px;height:32px}
.tasha-marketing .footer-logo img{height:27px;width:90px;object-fit:contain}
.tasha-marketing .footer-logo-sep{display:flex;align-items:center;gap:16px;padding:12px 0 4px}
.tasha-marketing .footer-logo-x{width:7px;height:7px}
.tasha-marketing .footer-links{display:flex;gap:48px}
.tasha-marketing .footer-links a{font-family:'Space Grotesk',sans-serif;font-size:10px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;color:#1a0f06;transition:opacity 0.2s}
.tasha-marketing .footer-links a:hover{opacity:0.5}
.tasha-marketing .footer-legal{font-family:'DM Sans',sans-serif;font-size:14px;line-height:20px;color:var(--text-ghost)}

/* ─── RESPONSIVE ────────────────────────────────────────── */

/* Tablet: 768–1199px */
@media(max-width:1199px){
  .tasha-marketing{--px:48px}
  .tasha-marketing .hero-inner{gap:24px;align-items:flex-start}
  .tasha-marketing .hero-left{gap:28px;padding:48px 0}
  .tasha-marketing .hero-right{margin-top:48px}
  .tasha-marketing .hero-photo{display:none}
  .tasha-marketing .hero-right{display:flex;flex-direction:column;align-items:flex-start;aspect-ratio:auto;max-height:none}
  .tasha-marketing .hero-blob-shadow{position:relative;width:100%;aspect-ratio:1}
  .tasha-marketing .hero-blob-bg{top:0;left:0}
  .tasha-marketing .obs-card{position:relative;width:215px;bottom:auto;left:auto;top:auto;margin-top:-40px;margin-left:20px}
  .tasha-marketing .hero-flowers{opacity:0.1;left:-28px;bottom:0;top:auto}
  .tasha-marketing .hero-img-decor{opacity:0.1}
  .tasha-marketing .hero-h1{font-size:clamp(48px,5.5vw,72px);line-height:1.1}
  .tasha-marketing .meditation-card{max-width:462px}
  .tasha-marketing .app-inner{gap:40px}
  .tasha-marketing .app-copy{flex:1 1 50%;min-width:0}
  .tasha-marketing .app-images{flex:1 1 45%;min-width:0;height:700px;width:auto}
  .tasha-marketing .tasha-card{width:100%;max-width:560px;right:var(--px);margin-right:0}
  .tasha-marketing .knots-h{font-size:38px;line-height:44px;white-space:normal;text-align:center}
  .tasha-marketing .knots-grid{gap:24px;flex-wrap:wrap}
  .tasha-marketing .knot-item{min-width:calc(50% - 12px)}
  .tasha-marketing .how-h{white-space:normal;font-size:40px;line-height:46px}
  .tasha-marketing .steps{flex-wrap:wrap;gap:40px}
  .tasha-marketing .step{min-width:calc(50% - 20px)}
  .tasha-marketing .steps-connector{display:none}
  .tasha-marketing .testi-row1{grid-template-columns:1fr 1fr;grid-template-rows:auto auto}
  .tasha-marketing .testi-big{grid-column:1/-1;grid-row:auto}
  .tasha-marketing .testi-row2{grid-template-columns:1fr 1fr}
  .tasha-marketing .testi-row2 .testi-card:last-child{grid-column:1/-1;max-width:50%;justify-self:center}
  .tasha-marketing .waitlist-h{font-size:40px;line-height:46px}
  .tasha-marketing .waitlist-form{max-width:460px}
  .tasha-marketing footer{height:auto;padding:32px var(--px);flex-direction:column;gap:24px;align-items:flex-start}
  .tasha-marketing nav{padding:0 var(--px)}
}

/* Mobile: <768px */
@media(max-width:767px){
  .tasha-marketing{--px:24px;--nav-h:68px}
  .tasha-marketing .nav-links{display:none}
  .tasha-marketing .btn-primary.nav-btn{display:none}
  .tasha-marketing .nav-hamburger{display:flex}
  .tasha-marketing .hero-inner{display:block;min-height:auto}
  .tasha-marketing .hero-left{max-width:100%;padding:24px 0 36px;gap:28px}
  .tasha-marketing .hero-right{display:none}
  .tasha-marketing .hero-mobile-photo{display:block}
  .tasha-marketing .hero-flowers{display:none}
  .tasha-marketing .hero-h1{font-size:36px;line-height:42px;letter-spacing:-1.5px}
  .tasha-marketing .hero-sub{font-size:15px;line-height:24px}
  .tasha-marketing .hero-ctas{flex-direction:column;width:100%}
  .tasha-marketing .btn-primary,.tasha-marketing .btn-secondary{justify-content:center;width:100%}
  .tasha-marketing .hero-stats{gap:16px;width:100%;flex-wrap:wrap}
  .tasha-marketing .hero-stats>div{flex:1;min-width:0}
  .tasha-marketing .stat-num{font-size:20px}
  .tasha-marketing .meditation-card-title{font-size:20px;line-height:26px}
  .tasha-marketing .banner-track{gap:20px}
  .tasha-marketing .app-inner{flex-direction:column;gap:0}
  .tasha-marketing .app-images{order:-1;width:100%;height:auto;display:grid;grid-template-columns:1fr 1fr;gap:12px;position:static;margin-bottom:36px;flex:none}
  .tasha-marketing .app-images-left,.tasha-marketing .app-images-right{display:flex;flex-direction:column;gap:12px}
  .tasha-marketing .app-img-74{position:static;width:100%;height:auto;border-radius:24px;overflow:hidden;box-shadow:var(--shadow-heavy)}
  .tasha-marketing .app-img-74 img{width:100%;height:auto;aspect-ratio:307/661;object-fit:cover;border-radius:24px}
  .tasha-marketing .app-img-77{position:static;width:100%;height:auto;overflow:hidden;border-radius:16px}
  .tasha-marketing .app-img-77 img{width:100%;height:auto;aspect-ratio:311/255;object-fit:cover}
  .tasha-marketing .app-img-76{position:static;width:100%;height:auto;overflow:hidden;box-shadow:var(--shadow-light);border-radius:16px}
  .tasha-marketing .app-img-76 img{width:100%;height:auto;aspect-ratio:280/203;object-fit:cover}
  .tasha-marketing .app-img-75{position:static;width:100%;height:auto;overflow:hidden;box-shadow:var(--shadow-light);border-radius:16px}
  .tasha-marketing .app-img-75 img{width:100%;height:auto;aspect-ratio:266/125;object-fit:cover}
  .tasha-marketing .app-copy{gap:20px;flex:none;width:100%}
  .tasha-marketing .app-h{font-size:42px;line-height:48px}
  .tasha-marketing .app-p{font-size:16px;line-height:26px}
  .tasha-marketing #tasha-section{height:auto;overflow:visible;display:flex;flex-direction:column;position:relative}
  .tasha-marketing .tasha-bg{position:relative;left:0;width:100%;height:auto;aspect-ratio:3/4;order:1;overflow:hidden}
  .tasha-marketing .tasha-bg img{object-position:center top}
  .tasha-marketing .lama-card{position:absolute;top:24px;left:24px;width:254px;height:auto;bottom:auto;z-index:5;animation:float 6s ease-in-out infinite}
  .tasha-marketing .tasha-card{position:relative;width:100%;max-width:none;right:auto;margin:0;padding:36px 24px;top:auto;bottom:auto;order:2;border-radius:0;background:#331710;backdrop-filter:none;margin-top:-60px;z-index:2}
  .tasha-marketing .tasha-h{font-size:36px;line-height:42px}
  .tasha-marketing .knots-h{font-size:32px;line-height:38px}
  .tasha-marketing .knots-grid{flex-direction:column;gap:0}
  .tasha-marketing .knot-item{border-top:1px solid var(--border-dark);padding-top:20px;padding-bottom:20px;gap:12px}
  .tasha-marketing .knot-name{font-size:28px;line-height:34px}
  .tasha-marketing .how-h{font-size:32px;line-height:38px}
  .tasha-marketing .how-p{font-size:16px;line-height:26px}
  .tasha-marketing .steps{flex-direction:column;gap:48px}
  .tasha-marketing .step{min-width:100%}
  .tasha-marketing .testi-row1,.tasha-marketing .testi-row2{grid-template-columns:1fr}
  .tasha-marketing .testi-row2 .testi-card:last-child{max-width:100%}
  .tasha-marketing .testi-quote{font-size:24px;line-height:32px}
  .tasha-marketing .waitlist-h{font-size:32px;line-height:38px}
  .tasha-marketing .waitlist-form{flex-direction:column;height:auto;gap:12px;width:100%}
  .tasha-marketing .waitlist-input{border-radius:9999px;height:52px}
  .tasha-marketing .waitlist-btn{border-radius:9999px;height:52px;justify-content:center;width:100%}
  .tasha-marketing footer{padding:28px var(--px)}
  .tasha-marketing .footer-links{flex-wrap:wrap;gap:16px}
  .tasha-marketing nav{padding:0 var(--px)}
  .tasha-marketing .testi-quote.sm{font-size:18px;line-height:26px}
  .tasha-marketing #knots{padding:72px var(--px)}
  .tasha-marketing #how{padding:72px var(--px) 48px}
  .tasha-marketing #testimonials{padding:72px var(--px)}
  .tasha-marketing #waitlist{padding:72px var(--px)}
}
      `}</style>
      <div className="tasha-marketing">
        {/* NAV */}
        <nav>
          <div className="nav-logo">
            <img src="/tasha-marketing/logo-weave.svg" alt="Weave" />
            <div className="nav-logo-sep">
              <svg
                className="nav-logo-x"
                width="7"
                height="7"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
              <span className="nav-tasha">Tasha</span>
            </div>
          </div>
          <div className="nav-links">
            <a href="#tasha-section">Meet Tasha</a>
            <a href="#knots">The Practice</a>
            <a href="#how">How It Works</a>
          </div>
          <a
            href="https://tally.so/r/ZjRayV"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary nav-btn"
          >
            Reserve your spot
          </a>
          <div
            className="nav-hamburger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
        </nav>

        <div
          className={`mobile-menu${mobileMenuOpen ? " open" : ""}`}
          id="mobileMenu"
        >
          <a href="#tasha-section" onClick={() => setMobileMenuOpen(false)}>
            Meet Tasha
          </a>
          <a href="#knots" onClick={() => setMobileMenuOpen(false)}>
            The Practice
          </a>
          <a href="#how" onClick={() => setMobileMenuOpen(false)}>
            How It Works
          </a>
          <a
            href="https://tally.so/r/ZjRayV"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileMenuOpen(false)}
          >
            {"Reserve your spot →"}
          </a>
        </div>

        {/* HERO */}
        <section id="hero">
          <div className="hero-flowers" aria-hidden="true">
            <img src="/tasha-marketing/hero-flowers.webp" alt="" />
          </div>
          <div className="hero-img-decor" aria-hidden="true">
            <img src="/tasha-marketing/hero-decor-topright.webp" alt="" />
          </div>

          <div className="hero-inner">
            <div className="hero-left">
              <div className="hero-copy reveal">
                <div className="eyebrow">
                  <span
                    className="eyebrow-line"
                    style={{ background: "#0f0f0f" }}
                  ></span>
                  <span className="eyebrow-text">
                    Weave{" "}
                    <span style={{ color: "var(--red)" }}>
                      × Lama Tasha Schumann
                    </span>
                  </span>
                </div>
                <div className="hero-mobile-photo" aria-hidden="true">
                  <svg
                    className="hero-mobile-shadow"
                    width="180"
                    height="180"
                    viewBox="0 0 603 603"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M588.451 359.149C528.753 558.01 383.772 627.17 230.263 595.743C76.7544 564.317 0 397.049 0 226.472C0 55.8936 166.301 -23.7169 341.131 6.16387C515.96 36.0447 648.148 160.288 588.451 359.149Z"
                      fill="#EBE5DF"
                    />
                  </svg>
                  <img src="/tasha-marketing/hero-blob-bg.webp" alt="" />
                </div>
                <h1 className="hero-h1 reveal d1">
                  Wisdom that <em>knows</em>
                  <br />
                  who you are
                </h1>
                <p className="hero-sub reveal d2">
                  Meditations designed for your specific journey. Experience{" "}
                  <strong>twenty years of ancient mastery</strong> with{" "}
                  <strong>Lama Tasha Schumann</strong>, a{" "}
                  <strong>Tibetan Buddhist teacher</strong>, personalized for
                  the life you&apos;re living right now.
                </p>
              </div>

              {/* Meditation card */}
              <div
                className="meditation-card reveal d2"
                ref={meditationCardRef}
              >
                <div className="meditation-card-blob">
                  <img
                    src="/tasha-marketing/meditation-blob.webp"
                    alt="Meet Tasha"
                  />
                </div>
                <div className="meditation-card-info">
                  <div className="meditation-card-title">Meet Tasha</div>
                  <div className="meditation-card-artist label-sm">
                    Tasha Schumann
                  </div>
                  <div className="meditation-card-meta meditation-card-meta-default">
                    <span>Introduction</span>
                    <span>•</span>
                    <span>3 MIN</span>
                  </div>
                  <div className="meditation-card-waveform" ref={waveformRef}>
                    {Array.from({ length: 16 }).map((_, i) => (
                      <div key={i} className="waveform-bar"></div>
                    ))}
                  </div>
                  <div className="meditation-card-progress">
                    <div
                      className="meditation-card-progress-track"
                      ref={progressTrackRef}
                    >
                      <div
                        className="meditation-card-progress-fill"
                        ref={progressFillRef}
                      ></div>
                      <div
                        className="meditation-card-progress-thumb"
                        ref={progressThumbRef}
                      ></div>
                    </div>
                    <div className="meditation-card-progress-time">
                      <span ref={timeCurrentRef}>0:00</span>
                      <span ref={timeTotalRef}>0:00</span>
                    </div>
                  </div>
                </div>
                <button
                  className="meditation-card-btn"
                  aria-label="Play"
                  onClick={handleMeditationClick}
                >
                  <svg
                    ref={playIconRef}
                    className="play-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    stroke="none"
                  >
                    <polygon points="6 3 20 12 6 21 6 3" />
                  </svg>
                  <svg
                    ref={pauseIconRef}
                    className="pause-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    stroke="none"
                    style={{ display: "none" }}
                  >
                    <rect x="5" y="3" width="4" height="18" />
                    <rect x="15" y="3" width="4" height="18" />
                  </svg>
                </button>
                <audio
                  ref={meditationAudioRef}
                  preload="none"
                  onLoadedMetadata={() => {
                    if (timeTotalRef.current && meditationAudioRef.current)
                      timeTotalRef.current.textContent = formatTime(
                        meditationAudioRef.current.duration,
                      );
                  }}
                  onTimeUpdate={updateProgress}
                  onEnded={resetMeditationPlayer}
                />
              </div>

              <div className="hero-ctas reveal d3">
                <a
                  href="https://tally.so/r/ZjRayV"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  Reserve your spot
                  <ArrowIcon />
                </a>
                <a href="#how" className="btn-secondary">
                  See how it works
                </a>
              </div>

              <div className="hero-stats reveal d4">
                <div>
                  <div className="stat-num">15</div>
                  <div className="stat-label">Beta members</div>
                </div>
                <div>
                  <div className="stat-num">20+</div>
                  <div className="stat-label">Years of practice</div>
                </div>
                <div>
                  <div className="stat-num">1:1</div>
                  <div className="stat-label">Direct access to Tasha</div>
                </div>
              </div>
            </div>

            {/* RIGHT visuals */}
            <div className="hero-right">
              <div className="hero-blob-shadow" aria-hidden="true">
                <svg
                  width="603"
                  height="603"
                  viewBox="0 0 603 603"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M588.451 359.149C528.753 558.01 383.772 627.17 230.263 595.743C76.7544 564.317 0 397.049 0 226.472C0 55.8936 166.301 -23.7169 341.131 6.16387C515.96 36.0447 648.148 160.288 588.451 359.149Z"
                    fill="#EBE5DF"
                  />
                </svg>
              </div>
              <div className="hero-blob-bg">
                <img src="/tasha-marketing/hero-blob-bg.webp" alt="" />
              </div>
              <div className="obs-card reveal d3">
                <div className="obs-eyebrow">
                  <span className="obs-eyebrow-text">Observation 01</span>
                  <span className="obs-eyebrow-line"></span>
                </div>
                <div className="obs-dot"></div>
                <p className="obs-quote">
                  &ldquo;I noticed you&apos;ve been sitting with the Striving a
                  lot lately. Let&apos;s try dropping the effort today. Just for
                  10 minutes.&rdquo;
                </p>
                <p className="obs-sig">— Tasha</p>
              </div>
            </div>
          </div>
        </section>

        {/* BANNER */}
        <div id="banner">
          <p className="banner-label">Tasha featured in</p>
          <div className="banner-track-wrap">
            <div className="banner-track" aria-hidden="true">
              {/* Duplicated for seamless marquee loop */}
              {[...BANNER_NAMES, ...BANNER_NAMES].map((name, i) => (
                <div key={i} className="banner-pill">
                  {name}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* APP FEATURES */}
        <section id="app">
          <div className="app-bg" aria-hidden="true">
            <img
              className="app-bg-img"
              src="/tasha-marketing/app-bg-texture.webp"
              alt=""
            />
            <div className="app-bg-grad"></div>
          </div>
          <div className="app-inner">
            <div className="app-copy reveal">
              <div className="eyebrow">
                <span
                  className="eyebrow-line"
                  style={{ background: "#0f0f0f" }}
                ></span>
                <span className="eyebrow-text" style={{ color: "var(--text)" }}>
                  Weave App
                </span>
              </div>
              <h2 className="app-h">
                A personal plan, <em>built around your psychology</em>
              </h2>
              <p className="app-p">
                Weave gives you a plan built from a deep assessment of your
                psychology, shaped by Tasha&apos;s direct input, and updates as
                you grow.
              </p>
              <p className="app-p">
                Tasha&apos;s teaching is woven into every practice: her voice,
                her framework, her direct input. Guidance that feels personal,
                available any time you need it.
              </p>
              <div className="highlights reveal d1">
                <div className="highlight">
                  <div className="highlight-title">
                    Personalized daily practices
                  </div>
                  <div className="highlight-desc">
                    Meditations shaped by your patterns and where you are right
                    now.
                  </div>
                </div>
                <div className="highlight">
                  <div className="highlight-title">
                    Tasha&apos;s wisdom, when you need it
                  </div>
                  <div className="highlight-desc">
                    Warmth, depth, and specificity — available any time.
                  </div>
                </div>
                <div className="highlight">
                  <div className="highlight-title">Evolves as you do</div>
                  <div className="highlight-desc">
                    Your plan adjusts as your practice deepens.
                  </div>
                </div>
              </div>
              <div className="quote-card reveal d2">
                <p className="quote-card-text">
                  I just feel an overwhelming sense of gratitude that one of my
                  two root teachers loves me enough to handcraft a practice for
                  me. I just feel so lucky.
                </p>
                <p className="quote-card-attr">— K.S., beta member</p>
              </div>
            </div>

            <div className="app-images reveal-fade d2">
              <div className="app-images-left">
                <div className="app-img-77">
                  <img
                    src="/tasha-marketing/app-img-77.webp"
                    alt="App feature"
                  />
                </div>
                <div className="app-img-75">
                  <img
                    src="/tasha-marketing/app-img-75.webp"
                    alt="App screen"
                  />
                </div>
                <div className="app-img-76">
                  <img
                    src="/tasha-marketing/app-img-76.webp"
                    alt="App screen"
                  />
                </div>
              </div>
              <div className="app-images-right">
                <div className="app-img-74">
                  <img
                    src="/tasha-marketing/app-img-74.webp"
                    alt="App screen"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TASHA */}
        <section id="tasha-section">
          <div className="tasha-bg">
            <img
              src="/tasha-marketing/tasha-photo.webp"
              alt="Lama Tasha Schumann"
            />
          </div>

          <div className="tasha-card reveal d1">
            <div className="eyebrow">
              <span
                className="eyebrow-line"
                style={{ background: "var(--text-ghost)" }}
              ></span>
              <span
                className="eyebrow-text"
                style={{ color: "var(--red-light)" }}
              >
                Your Teacher
              </span>
            </div>
            <h2 className="tasha-h">
              Ancient wisdom...
              <br />
              <em>for your actual life</em>
            </h2>
            <div className="tasha-body">
              <strong>Tasha is a Tibetan Buddhist Lama</strong> trained in the
              direct-mind traditions of Dzogchen and Mahamudra: lineages that
              have been mapping the architecture of the human mind for over two
              thousand years.
              {"\n\n"}
              She&apos;s also an artist, a podcaster, a cultural catalyzer.
              She&apos;s appeared on world stages. She publishes{" "}
              <em>Bodhisavage</em>, a Substack on dharma in the midst of
              culture. Her work is about cutting through the noise to uncover
              the clarity, connection, and creativity at the heart of being
              human.
              {"\n\n"}
              Her gift, and the reason this beta exists, is making the practice
              feel immediately alive, and then making it <em>yours</em>.
            </div>
            <div className="tasha-pills-wrap">
              <p className="tasha-pills-label">Also known as:</p>
              <div className="tasha-pills">
                <a
                  className="tasha-pill"
                  href="https://tashaschumann.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  tashaschumann.com
                </a>
                <a
                  className="tasha-pill"
                  href="https://bodhisavage.substack.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Bodhisavage
                </a>
                <a
                  className="tasha-pill"
                  href="https://www.mindbodpod.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Mind Bod Adventure Pod
                </a>
              </div>
            </div>
          </div>

          <div className="lama-card reveal d2">
            <div className="lama-card-label">Lama Tasha Schumann</div>
            <ul>
              <li>Dzogchen &amp; Mahamudra lineages</li>
              <li>20+ years practice</li>
              <li>Artist &amp; Podcast host</li>
            </ul>
          </div>
        </section>

        {/* KNOTS */}
        <section id="knots">
          <div className="knots-copy reveal">
            <div
              className="eyebrow knots-eyebrow"
              style={{ justifyContent: "center" }}
            >
              <span
                className="eyebrow-text"
                style={{ color: "var(--red-light)" }}
              >
                The framework
              </span>
            </div>
            <h2 className="knots-h">
              The places where you get stuck are{" "}
              <em>where your wisdom is hiding</em>
            </h2>
            <p className="knots-p">
              Tasha&apos;s teaching is rooted in Tibetan Buddhist traditions
              that have been mapping the architecture of the human mind for over
              2,000 years. She calls the patterns that hold us back Knots. There
              are five. You&apos;ll recognize at least one.
            </p>
          </div>

          <div className="knots-grid">
            {KNOTS_DATA.map((knot) => (
              <div key={knot.num} className={`knot-item reveal ${knot.delay}`}>
                <div className="knot-num">{knot.num}</div>
                <div className="knot-name">{knot.name}</div>
                <p className="knot-desc">{knot.desc}</p>
              </div>
            ))}
          </div>

          <div className="knots-cta reveal">
            <p className="knots-cta-note">
              In the app, each Knot has its own practice set
            </p>
            <a
              href="https://tally.so/r/ZjRayV"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-dark"
            >
              Discover your patterns
              <ArrowIcon />
            </a>
            <p className="knots-cta-note">
              Your assessment tells you which ones are shaping your life
            </p>
          </div>
        </section>

        {/* WHAT YOU GET */}
        <section id="how">
          <div className="how-header reveal">
            <div className="eyebrow" style={{ justifyContent: "center" }}>
              <span className="eyebrow-text" style={{ color: "var(--text)" }}>
                What happens when you join
              </span>
            </div>
            <h2 className="how-h">
              From intake to insight <em>in under 10 minutes</em>
            </h2>
            <p className="how-p">
              The assessment isn&apos;t a quiz. It&apos;s a conversation that
              surfaces the patterns shaping your life, often things you
              couldn&apos;t name yourself. Tasha reviews the results and builds
              your plan around them.
            </p>
          </div>

          <div className="steps">
            <div className="steps-connector" aria-hidden="true"></div>

            {/* Step 01 */}
            <div className="step reveal d1">
              <div className="step-image">
                <img
                  src="/tasha-marketing/process-1.webp"
                  alt="Deep assessment journal preview"
                />
                <div className="step-image-scrim" aria-hidden="true"></div>
              </div>
              <div className="step-copy">
                <div className="step-title">Deep Assessment</div>
                <p className="step-desc">
                  Answer a detailed set of questions about your psychology,
                  patterns, and practice history. The system surfaces things you
                  might not have been able to name yourself.
                </p>
                <div className="step-tag">Takes ~10 min</div>
              </div>
            </div>

            {/* Step 02 */}
            <div className="step reveal d2">
              <div className="step-image">
                <img
                  src="/tasha-marketing/process-2.webp"
                  alt="Your assessment report preview"
                />
                <div className="step-image-scrim" aria-hidden="true"></div>
              </div>
              <div className="step-copy">
                <div className="step-title">Your Report</div>
                <p className="step-desc">
                  The system generates a psychological profile — surfacing
                  patterns, naming your primary Knots, and mapping your natural
                  entry point.
                </p>
                <div className="step-tag">Your living experience</div>
              </div>
            </div>

            {/* Step 03 */}
            <div className="step reveal d3">
              <div className="step-image">
                <img
                  src="/tasha-marketing/process-3.webp"
                  alt="Your personalized pathway from Tasha"
                />
                <div className="step-image-scrim" aria-hidden="true"></div>
              </div>
              <div className="step-copy">
                <div className="step-title">Your Plan, from Tasha</div>
                <p className="step-desc">
                  Tasha reviews your profile and co-creates a practice plan
                  tailored to your specific points of friction. Your name is in
                  it. Your life is in it.
                </p>
                <div className="step-tag">Tasha&apos;s direct input</div>
              </div>
            </div>

            {/* Step 04 */}
            <div className="step reveal d4">
              <div className="step-image">
                <img
                  src="/tasha-marketing/process-4.webp"
                  alt="Weekly live community call"
                />
                <div className="step-image-scrim" aria-hidden="true"></div>
              </div>
              <div className="step-copy">
                <div className="step-title">
                  Ongoing Support &amp; Evolution
                </div>
                <p className="step-desc">
                  Your plan keeps evolving — Tasha stays with it, updating your
                  path as you grow. Weekly live calls bring a small group
                  together for dharma, a guided sit, and open discussion.
                </p>
                <div className="step-tag">Tasha + weekly live community</div>
              </div>
            </div>
          </div>

          <div
            className="how-cta reveal"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              alignItems: "center",
              marginTop: 64,
            }}
          >
            <a
              href="https://tally.so/r/ZjRayV"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ height: 48, padding: "12px 36px" }}
            >
              Sign up for a free assessment
              <ArrowIcon />
            </a>
            <p
              style={{
                fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif",
                fontSize: 14,
                lineHeight: "22px",
                color: "var(--text-ghost)",
                textAlign: "center",
              }}
            >
              Takes about 10 minutes. No commitment.
            </p>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section id="testimonials">
          <div className="testi-header reveal">
            <div className="eyebrow" style={{ justifyContent: "center" }}>
              <span className="eyebrow-text" style={{ color: "var(--text)" }}>
                What beta members say
              </span>
            </div>
            <h2 className="testi-h">Real people, real change</h2>
          </div>

          <div className="testi-grid">
            <div className="testi-row1">
              <TestimonialCard
                quote={
                  "\u201CTasha helped me to re-fall in love with the animal warmth, soft beauty of my body... and gave me the impetus to reach back and love on that person who has used some unhealthy coping mechanisms because she just didn\u2019t know how.\u201D"
                }
                name="K.S."
                role="beta member"
                variant="dark"
                className="testi-big"
                style={{ minHeight: 448, justifyContent: "space-between" }}
                quoteStyle={{
                  fontSize: 36,
                  lineHeight: "46px",
                  letterSpacing: "-0.36px",
                  opacity: 0.8,
                }}
              />
              <TestimonialCard
                quote={
                  "\u201CIn the past couple weeks I\u2019ve had two different people I really respect just say, whoa, you are so grounded. Whoa, you\u2019re so embodied.\u201D"
                }
                name="K.S."
                role="beta member"
                variant="light"
                delay="d1"
                style={{ minHeight: 212 }}
              />
              <TestimonialCard
                quote={
                  "\u201CI love your voice, Tasha. It\u2019s such a delight in practicing with you.\u201D"
                }
                name="A."
                role="beta member"
                variant="light"
                delay="d2"
                style={{ minHeight: 212 }}
              />
            </div>

            <div className="testi-row2" style={{ marginTop: 24 }}>
              <TestimonialCard
                quote={
                  "\u201CThere was one that I just stopped and deleted and did again because I was like, holy sh*t \u2014 this actually made me realize something I couldn\u2019t originally name. And then it put it into the path. That thing feels like life work.\u201D"
                }
                name="J.M."
                role="beta member"
                variant="light"
                delay="d1"
                style={{ minHeight: 222 }}
              />
              <TestimonialCard
                quote={
                  "\u201CThe whole thing on urgency is just really speaking to me. I had no idea. Even with meditating, doing yoga for years, teaching all this stuff to kids \u2014 I always lead with this weird sense of urgency. Once you named it for me, I was like, oh, that\u2019s it.\u201D"
                }
                name="B."
                role="beta member"
                variant="light"
                delay="d2"
                style={{ minHeight: 222 }}
              />
              <TestimonialCard
                quote={
                  "\u201CInstead of freaking out, I ate, I walked, I meditated. It\u2019s gonna be okay.\u201D"
                }
                name="P.S."
                role="beta member"
                variant="light"
                delay="d3"
                style={{ minHeight: 222 }}
              />
            </div>
          </div>
        </section>

        {/* WAITLIST */}
        <section id="waitlist">
          <div className="waitlist-glow" aria-hidden="true"></div>
          <div className="waitlist-inner reveal">
            <h2 className="waitlist-h">
              Spots are limited — reserve your spot
            </h2>
            <p className="waitlist-p">
              We&apos;re opening access to a small group ready to go deeper.
              Sign up to be first — and to get early access to Tasha&apos;s free
              assessment.
            </p>
            <a
              href="https://tally.so/r/ZjRayV"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                height: 48,
                padding: "12px 36px",
                background: "#faf6f0",
                color: "#1a1a1a",
              }}
            >
              Reserve your spot
              <ArrowIcon />
            </a>
            <p className="waitlist-fine">
              No spam. No pressure. Just a heads-up when we&apos;re ready for
              you.
            </p>
          </div>
        </section>

        {/* FOOTER */}
        <footer>
          <div className="footer-logo">
            <img src="/tasha-marketing/logo-weave.svg" alt="Weave" />
            <div className="footer-logo-sep">
              <svg
                className="footer-logo-x"
                width="7"
                height="7"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
              <span className="nav-tasha">Tasha</span>
            </div>
          </div>
          <div className="footer-links">
            <Link href="/">Weave</Link>
            <a
              href="https://www.meditatehappier.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Happier Meditation
            </a>
          </div>
          <p className="footer-legal">© 2026 Weave / Happier Meditation</p>
        </footer>
      </div>
    </>
  );
}
